import { inSeason, seasonLabel } from '../utils/season';
import React from 'react';
import TrophySvg from './TrophySvg';

/* ====================================================================
   CLUB ANALYTICS PANEL — all-time performance dashboard
   ==================================================================== */
function ClubAnalyticsPanel({ dashLeaderboard, prevYearLeaderboard, comps, compYearFilter, seasonStartMonth = 1 }) {
  const standings = (dashLeaderboard && dashLeaderboard.standings) || [];
  const players = standings.filter(s => s.type === 'player');
  const teamEntries = standings.filter(s => s.type === 'team');
  const selectedYear = parseInt(compYearFilter, 10);
  const previousYear = Number.isNaN(selectedYear) ? null : (selectedYear - 1);
  const prevPlayers = ((prevYearLeaderboard && prevYearLeaderboard.standings) || []).filter(s => s.type === 'player');
  const prevPlayersByKey = new Map(prevPlayers.map(p => [
    (p.id !== undefined && p.id !== null) ? `id:${String(p.id)}` : `name:${String(p.name || '').toLowerCase()}`,
    p,
  ]));

  // --- Key stats ---
  const totalPlayers = players.length;
  const totalTeams = teamEntries.length;
  const totalMatches = standings.reduce((a, s) => a + (s.played || 0), 0);
  const filteredComps = comps ? comps.filter(c => c.status === 'completed' && (!compYearFilter || compYearFilter === 'all' || (c.startDate && inSeason(c.startDate, compYearFilter, seasonStartMonth)))) : [];
  const totalCompetitions = filteredComps.length;

  // --- Top performers ---
  const topByRating = [...players].sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 5);
  const topByWins = [...players].sort((a, b) => (b.wins || 0) - (a.wins || 0)).slice(0, 5);
  const topByWinRate = [...players].filter(s => (s.played - (s.byes || 0)) >= 3).sort((a, b) => {
    const awr = (a.played - (a.byes || 0)) > 0 ? a.wins / (a.played - (a.byes || 0)) : 0;
    const bwr = (b.played - (b.byes || 0)) > 0 ? b.wins / (b.played - (b.byes || 0)) : 0;
    return bwr - awr;
  }).slice(0, 5);
  const topByCompWins = [...standings].filter(s => (s.compWins || 0) > 0).sort((a, b) => (b.compWins || 0) - (a.compWins || 0));

  // --- Most improved players (previous year -> selected year) ---
  const mostImprovedPlayers = Number.isNaN(selectedYear)
    ? []
    : players
      .map((p) => {
        const key = (p.id !== undefined && p.id !== null) ? `id:${String(p.id)}` : `name:${String(p.name || '').toLowerCase()}`;
        const prev = prevPlayersByKey.get(key);
        if (!prev) return null;
        const currPlayed = (p.played || 0) - (p.byes || 0);
        const prevPlayed = (prev.played || 0) - (prev.byes || 0);
        const currRating = Number(p.rating) || 0;
        const prevRating = Number(prev.rating) || 0;
        const delta = currRating - prevRating;
        if (currPlayed < 3 || prevPlayed < 3 || delta <= 0) return null;
        return {
          id: p.id,
          name: p.name,
          delta,
          prevRating,
          currRating,
          prevPlayed,
          currPlayed,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (b.delta !== a.delta) return b.delta - a.delta;
        return b.currRating - a.currRating;
      })
      .slice(0, 5);

  // --- Aggregate results ---
  const totalWins = standings.reduce((a, s) => a + (s.wins || 0), 0);
  const totalDraws = standings.reduce((a, s) => a + (s.draws || 0), 0);
  const totalLosses = standings.reduce((a, s) => a + (s.losses || 0), 0);
  const totalResults = totalWins + totalDraws + totalLosses || 1;
  const winPct = ((totalWins / totalResults) * 100).toFixed(1);
  const drawPct = ((totalDraws / totalResults) * 100).toFixed(1);
  const lossPct = ((totalLosses / totalResults) * 100).toFixed(1);

  // --- Rating distribution ---
  const ratedPlayers = players.filter(p => (p.rating || 0) > 0);
  const ratingBuckets = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90];
  const ratingDist = ratingBuckets.map(low => ({
    label: `${low}-${low + 9}`,
    count: ratedPlayers.filter(p => p.rating >= low && p.rating < low + 10).length,
  })).filter(b => b.count > 0);
  const maxRatingCount = Math.max(...ratingDist.map(b => b.count), 1);

  // --- Points diff bar chart (top 10 players) ---
  const top10byDiff = [...players].map(p => ({ ...p, diff: (p.pointsFor || 0) - (p.pointsAgainst || 0) })).sort((a, b) => b.diff - a.diff).slice(0, 10);
  const maxAbsDiff = top10byDiff.length ? Math.max(...top10byDiff.map(s => Math.abs(s.diff)), 1) : 1;

  // --- Shots for bar chart (top 10) ---
  const top10byShots = [...players].sort((a, b) => (b.pointsFor || 0) - (a.pointsFor || 0)).slice(0, 10);
  const maxShots = top10byShots.length ? Math.max(...top10byShots.map(s => s.pointsFor || 0), 1) : 1;

  // --- Ends won bar chart (top 10) ---
  const top10byEnds = [...players].sort((a, b) => (b.endsWon || 0) - (a.endsWon || 0)).slice(0, 10);
  const maxEndsVal = top10byEnds.length ? Math.max(...top10byEnds.map(s => s.endsWon || 0), 1) : 1;

  return (
    <div>
      {standings.length === 0 ? (
        <p className="w3-text-grey w3-center" style={{ padding: 32 }}><em>No data yet. Complete some competitions to see analytics.</em></p>
      ) : (<>

      {/* === KEY STATS (inline) === */}
      <div className="analytics-stats-grid" style={{ marginTop: 8 }}>
        <div className="analytics-stat-card w3-pale-green">
          <i className="fa fa-users analytics-stat-icon w3-text-green"></i>
          <div className="analytics-stat-value">{totalPlayers}</div>
          <div className="analytics-stat-label">Players</div>
        </div>
        <div className="analytics-stat-card w3-pale-blue">
          <i className="fa fa-shield analytics-stat-icon w3-text-blue"></i>
          <div className="analytics-stat-value">{totalTeams}</div>
          <div className="analytics-stat-label">Teams</div>
        </div>
        <div className="analytics-stat-card w3-pale-yellow">
          <i className="fa fa-futbol-o analytics-stat-icon w3-text-orange"></i>
          <div className="analytics-stat-value">{Math.floor(totalMatches / 2)}</div>
          <div className="analytics-stat-label">Total Match Results</div>
        </div>
        <div className="analytics-stat-card w3-pale-red">
          <i className="fa fa-trophy analytics-stat-icon w3-text-red"></i>
          <div className="analytics-stat-value">{totalCompetitions}</div>
          <div className="analytics-stat-label">Competitions Completed</div>
        </div>
      </div>

      {/* === ROW 2: Top Rated, Win Rate, Most Wins, Result Distribution, Rating Distribution === */}
      <div className="analytics-quint-grid">
        {/* Top by Rating */}
        {topByRating.length > 0 && (
          <div className="w3-card w3-round w3-light-grey w3-margin-top" style={{ padding: '16px' }}>
            <h5 style={{ marginTop: 0 }}><i className="fa fa-star w3-text-amber" style={{ marginRight: 8 }}></i>Top Rated Players</h5>
            {topByRating.map((p, i) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: i < topByRating.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                <span style={{ width: 24, fontWeight: 700, color: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : '#999', fontSize: 'var(--gs-text-sm)' }}>#{i + 1}</span>
                <span style={{ flex: 1, fontWeight: 600, fontSize: 'var(--gs-text-sm)' }}>{p.name}</span>
                <span style={{ fontWeight: 700, fontSize: 'var(--gs-text-sm)' }}>{(p.rating || 0).toFixed(1)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Best Win Rate (min 3 matches) */}
        {topByWinRate.length > 0 && (
          <div className="w3-card w3-round w3-light-grey w3-margin-top" style={{ padding: '16px' }}>
            <h5 style={{ marginTop: 0 }}><i className="fa fa-percent w3-text-green" style={{ marginRight: 8 }}></i>Best Win Rate <span className="w3-tiny w3-text-grey">(min 3)</span></h5>
            {topByWinRate.map((p, i) => {
              const wr = (p.played - (p.byes || 0)) > 0 ? ((p.wins / (p.played - (p.byes || 0))) * 100).toFixed(0) : 0;
              return (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: i < topByWinRate.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                  <span style={{ width: 24, fontWeight: 700, color: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : '#999', fontSize: 'var(--gs-text-sm)' }}>#{i + 1}</span>
                  <span style={{ flex: 1, fontWeight: 600, fontSize: 'var(--gs-text-sm)' }}>{p.name}</span>
                  <span style={{ fontWeight: 700, fontSize: 'var(--gs-text-sm)' }}>{wr}%</span>
                  <span className="w3-tiny w3-text-grey">({p.wins}W/{p.played - (p.byes || 0)}P)</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Most Wins */}
        {topByWins.length > 0 && (
          <div className="w3-card w3-round w3-light-grey w3-margin-top" style={{ padding: '16px' }}>
            <h5 style={{ marginTop: 0 }}><i className="fa fa-trophy w3-text-blue" style={{ marginRight: 8 }}></i>Most Wins</h5>
            {topByWins.map((p, i) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: i < topByWins.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                <span style={{ width: 24, fontWeight: 700, color: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : '#999', fontSize: 'var(--gs-text-sm)' }}>#{i + 1}</span>
                <span style={{ flex: 1, fontWeight: 600, fontSize: 'var(--gs-text-sm)' }}>{p.name}</span>
                <span style={{ fontWeight: 700, fontSize: 'var(--gs-text-sm)' }}>{p.wins} wins</span>
              </div>
            ))}
          </div>
        )}

        {/* Most Improved */}
        {!Number.isNaN(selectedYear) && mostImprovedPlayers.length > 0 && (
          <div className="w3-card w3-round w3-light-grey w3-margin-top" style={{ padding: '16px' }}>
            <h5 style={{ marginTop: 0 }}>
              <i className="fa fa-line-chart w3-text-green" style={{ marginRight: 8 }}></i>
              Most Improved Players
              {previousYear && (
                <span className="w3-tiny w3-text-grey" style={{ marginLeft: 8 }}>
                  ({seasonLabel(previousYear, seasonStartMonth)} to {seasonLabel(selectedYear, seasonStartMonth)})
                </span>
              )}
            </h5>
            {mostImprovedPlayers.map((p, i) => (
              <div key={p.id || `${p.name}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: i < mostImprovedPlayers.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                <span style={{ width: 24, fontWeight: 700, color: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : '#999', fontSize: 'var(--gs-text-sm)' }}>#{i + 1}</span>
                <span style={{ flex: 1, fontWeight: 600, fontSize: 'var(--gs-text-sm)' }}>{p.name}</span>
                <span className="w3-tag w3-round w3-green" style={{ fontWeight: 700 }}>+{p.delta.toFixed(1)}</span>
                <span className="w3-tiny w3-text-grey">{p.prevRating.toFixed(1)} to {p.currRating.toFixed(1)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Rating Distribution */}
        {ratingDist.length > 0 && (
          <div className="w3-card w3-round w3-light-grey w3-margin-top" style={{ padding: '16px' }}>
            <h5 style={{ marginTop: 0 }}><i className="fa fa-signal w3-text-theme" style={{ marginRight: 8 }}></i>Rating Distribution</h5>
            <div className="analytics-round-chart">
              {ratingDist.map((b, i) => (
                <div key={b.label} className="analytics-round-bar-col">
                  <div className="analytics-round-bar-value">{b.count}</div>
                  <div className="analytics-round-bar-track">
                    <div className="analytics-round-bar-fill" style={{
                      height: `${(b.count / maxRatingCount) * 100}%`,
                      background: `hsl(${120 + i * 25}, 55%, 50%)`,
                    }}></div>
                  </div>
                  <div className="analytics-round-bar-label">{b.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* === ROW 3: Shots / Diff / Ends / Comp Winners — quad grid === */}
      <div className="analytics-quad-grid">

      {/* === SHOTS FOR (TOP 10) === */}
      {top10byShots.length > 0 && (
        <div className="w3-card w3-round w3-light-grey w3-margin-top" style={{ padding: '16px' }}>
          <h5 style={{ marginTop: 0 }}><i className="fa fa-bullseye w3-text-theme" style={{ marginRight: 8 }}></i>Top 10 — Shots Scored</h5>
          <div className="analytics-chart-container">
            {top10byShots.map((s, i) => (
              <div key={s.id} className="analytics-bar-row">
                <div className="analytics-bar-label" title={s.name}>{s.name}</div>
                <div className="analytics-bar-track">
                  <div className="analytics-bar-fill" style={{
                    width: `${((s.pointsFor || 0) / maxShots) * 100}%`,
                    background: i === 0 ? '#4CAF50' : i === 1 ? '#66BB6A' : i === 2 ? '#81C784' : '#90CAF9',
                  }}>
                    <span className="analytics-bar-value">{s.pointsFor}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === POINTS DIFFERENTIAL (TOP 10) === */}
      {top10byDiff.length > 0 && (
        <div className="w3-card w3-round w3-light-grey w3-margin-top" style={{ padding: '16px' }}>
          <h5 style={{ marginTop: 0 }}><i className="fa fa-arrows-h w3-text-theme" style={{ marginRight: 8 }}></i>Top 10 — Points Differential</h5>
          <div className="analytics-chart-container">
            {top10byDiff.map((s) => {
              const pct = (s.diff / maxAbsDiff) * 50;
              const isPositive = s.diff >= 0;
              return (
                <div key={s.id} className="analytics-diff-row">
                  <div className="analytics-bar-label" title={s.name}>{s.name}</div>
                  <div className="analytics-diff-track">
                    <div className="analytics-diff-center"></div>
                    {isPositive ? (
                      <div className="analytics-diff-fill analytics-diff-positive" style={{ left: '50%', width: `${pct}%` }}>
                        <span className="analytics-diff-value">+{s.diff}</span>
                      </div>
                    ) : (
                      <div className="analytics-diff-fill analytics-diff-negative" style={{ right: '50%', width: `${Math.abs(pct)}%` }}>
                        <span className="analytics-diff-value">{s.diff}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* === ENDS WON (TOP 10) === */}
      {top10byEnds.length > 0 && (
        <div className="w3-card w3-round w3-light-grey w3-margin-top" style={{ padding: '16px' }}>
          <h5 style={{ marginTop: 0 }}><i className="fa fa-flag-checkered w3-text-theme" style={{ marginRight: 8 }}></i>Top 10 — Ends Won</h5>
          <div className="analytics-chart-container">
            {top10byEnds.map((s, i) => (
              <div key={s.id} className="analytics-bar-row">
                <div className="analytics-bar-label" title={s.name}>{s.name}</div>
                <div className="analytics-bar-track">
                  <div className="analytics-bar-fill" style={{
                    width: `${((s.endsWon || 0) / maxEndsVal) * 100}%`,
                    background: `hsl(${260 + i * 15}, 60%, 60%)`,
                  }}>
                    <span className="analytics-bar-value">{s.endsWon}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Competition Winners */}
      {topByCompWins.length > 0 && (
        <div className="w3-card w3-round w3-light-grey w3-margin-top" style={{ padding: '16px' }}>
          <h5 style={{ marginTop: 0 }}><TrophySvg size={16} color="gold" style={{ marginRight: 8 }} />Competition Winners</h5>
          {topByCompWins.map((p, i) => (
            <div key={`${p.type}-${p.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: i < topByCompWins.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
              <span style={{ width: 24, fontWeight: 700, color: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : '#999', fontSize: 'var(--gs-text-sm)' }}>#{i + 1}</span>
              <span style={{ flex: 1, fontWeight: 600, fontSize: 'var(--gs-text-sm)' }}>
                <i className={`fa ${p.type === 'team' ? 'fa-shield' : 'fa-user'} w3-margin-right w3-small`} style={{ color: '#999', width: 14 }}></i>
                {p.name}
              </span>
              <span style={{ fontWeight: 700, fontSize: 'var(--gs-text-sm)' }}>{p.compWins} title{p.compWins !== 1 ? 's' : ''}</span>
            </div>
          ))}
        </div>
      )}

      </div>{/* end quad grid */}

      </>)}
    </div>
  );
}

export default ClubAnalyticsPanel;
